import { Extension } from "@tiptap/core"
import { Plugin } from "@tiptap/pm/state"
import type * as filestack from "filestack-js"
import { reportOperation } from "../../analytics/operation-event"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { asAttachmentId, type EditorSurface } from "../../analytics/types"
import { insertBlockContent } from "../../util/insert-block-content"
import { prepareFileUploads, uploadFiles } from "./file-upload"

export interface FileUploadOptions {
  filestackClient: filestack.Client | null
}

/**
 * Uploads non-image files dropped/pasted into the editor to Filestack, immediately inserting each
 * as a linkBlock whose URL is filled in on completion.
 * Image files are instead handled by the Image extension
 * (image-node-extension.ts) — this extension only acts on files whose mimetype isn't
 * image/*, so a single drop/paste containing both kinds is fully handled between the two.
 */
export const ExtensionFileUpload = Extension.create<FileUploadOptions>({
  name: "fileUpload",
  addOptions() {
    return { filestackClient: null }
  },

  addProseMirrorPlugins() {
    const editor = this.editor
    const { filestackClient } = this.options

    function upload(files: File[], surface: EditorSurface, pos?: number): void {
      if (files.length === 0 || !filestackClient) return
      const tasks = prepareFileUploads(files)
      insertBlockContent(
        editor,
        tasks.map(({ link }) => ({ type: "linkBlock", attrs: link })),
        pos,
      )

      const [first, ...rest] = tasks.map(({ link }) => asAttachmentId(link.attachmentId))
      if (first === undefined) return
      reportOperation(editor, OPERATION_CONTRACTS.OP_INSERT_FILE, surface, {
        kind: "attachment",
        fileIds: [first, ...rest],
        source: "upload",
      })
      void uploadFiles(editor, filestackClient, tasks)
    }

    return [
      new Plugin({
        props: {
          handleDrop(view, event) {
            if (!editor.isEditable || !event.dataTransfer) return false

            const files = nonImageFiles(event.dataTransfer.files)
            if (files.length === 0) return false

            event.preventDefault()
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
            upload(files, "file-drop", pos)

            // Return false so that the Image handler can also run.
            return false
          },

          handlePaste(_view, event) {
            if (!editor.isEditable || !event.clipboardData) return false

            const files = nonImageFiles(event.clipboardData.files)
            if (files.length === 0) return false

            event.preventDefault()
            upload(files, "clipboard")

            // Return false so that the Image handler can also run.
            return false
          },
        },
      }),
    ]
  },
})

function nonImageFiles(fileList: FileList): File[] {
  return [...fileList].filter((file) => !file.type.startsWith("image/"))
}

export default ExtensionFileUpload
